import { useCallback } from 'react';

import { useIsClusterDemoMode } from '@cast/infrastructure-demo';
import { useAbility } from '@cast/shared-ability-data-access';
import { Cluster } from 'types/cluster';

import { useCastctlOnboardingDialog } from './dialogs/cluster/useCastctlOnboardingDialog';

import {
  useCluster,
  isAKSCluster,
  isKOPSCluster,
  isClusterOnboardingPhase2,
  isKentManagedCluster,
} from '@cast/shared-cluster-context-data-access';

type State = {
  canBeEnabled: boolean;
  hasBeenEnabled: boolean;
  hasPermissionsToEnable: boolean;
  enable(): void;
};

// exported for tests only
export function computeState(context: {
  cluster: Cluster;
  isManaged: boolean;
  canBeManaged: boolean;
  isInDemoMode: boolean;
  isKentEligible: boolean;
}) {
  const { cluster, isManaged, canBeManaged, isInDemoMode, isKentEligible } =
    context;

  const isKops = isKOPSCluster(cluster);
  const isAks = isAKSCluster(cluster);
  const isKentManaged = isKentManagedCluster(cluster);
  const clusterIsConnecting = isClusterOnboardingPhase2(
    cluster.status,
    isManaged
  );
  const clusterOnboarded = isAks
    ? !clusterIsConnecting && isManaged
    : isManaged;

  const hasBeenEnabled = clusterOnboarded || isKentManaged;
  const canBeEnabled =
    !isKops &&
    !hasBeenEnabled &&
    canBeManaged &&
    !isInDemoMode &&
    !isKentEligible;
  return { hasBeenEnabled, canBeEnabled } as const;
}

export function useEnableCastAiButtonState(): State {
  const { cluster, isManaged, canBeManaged, isKentEligible } = useCluster();
  const isInDemoMode = useIsClusterDemoMode();
  const { openCastctlOnboardingDialog } = useCastctlOnboardingDialog();

  const hasPermissionsToEnable = useAbility().can('update', 'Clusters');

  const { hasBeenEnabled, canBeEnabled } = computeState({
    cluster,
    isManaged,
    canBeManaged,
    isInDemoMode,
    isKentEligible,
  });
  const handleEnable = useCallback(() => {
    openCastctlOnboardingDialog({
      cluster,
      skipConnectionMonitoring: true,
    });
  }, [cluster, openCastctlOnboardingDialog]);

  return {
    enable: handleEnable,
    hasPermissionsToEnable,
    hasBeenEnabled,
    canBeEnabled,
  };
}
